import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl shadow-[0_4px_20px_rgba(26,29,26,0.04)]"
          : "bg-transparent"
      }`}
    >
      <div className="container-luxe flex h-20 items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-brand-link"
          className="group flex items-baseline gap-2"
        >
          <span className="font-[Cormorant_Garamond] text-2xl font-semibold tracking-tight text-[#1A1D1A]">
            Sagita Rasmadi
          </span>
          <span className="hidden text-[10px] uppercase tracking-[0.3em] text-[#C6A66B] sm:inline">
            LLC
          </span>
        </Link>

        <nav className="hidden items-center gap-10 md:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative text-sm font-medium tracking-wide transition-colors ${
                  isActive
                    ? "text-[#7A8C7B]"
                    : "text-[#1A1D1A] hover:text-[#7A8C7B]"
                }`
              }
            >
              {({ isActive }) => (
                <span className="inline-flex flex-col items-center">
                  {l.label}
                  <span
                    className={`mt-1 h-px transition-all duration-300 ${
                      isActive ? "w-6 bg-[#C6A66B]" : "w-0 bg-[#C6A66B]"
                    }`}
                  />
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <Link
          to="/contact"
          data-testid="navbar-cta-consultation"
          className="hidden btn-primary md:inline-flex"
          style={{ paddingTop: 10, paddingBottom: 10 }}
        >
          Request Consultation
        </Link>

        <button
          type="button"
          data-testid="navbar-mobile-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="rounded-full border border-[#D9CFC2] bg-white p-2.5 text-[#1A1D1A] md:hidden"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="md:hidden"
            data-testid="navbar-mobile-menu"
          >
            <div className="border-t border-[#D9CFC2]/60 bg-white/95 backdrop-blur-xl">
              <div className="container-luxe flex flex-col py-4">
                {links.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `py-3 text-base font-medium ${
                        isActive ? "text-[#7A8C7B]" : "text-[#1A1D1A]"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
